import { Link } from 'react-router-dom';
import { categoryToSlug, isNewPost } from '@/utils/blogUtils';
import ReadTimeIndicator from './ReadTimeIndicator';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
}

interface BlogCardProps {
  post: BlogPost;
  index: number;
  isVisible: boolean;
  maxReadMinutes: number;
  /** 'normal' = standard grid card; 'featured' = wide hero card */
  variant?: 'normal' | 'featured';
}

export default function BlogCard({
  post,
  index,
  isVisible,
  maxReadMinutes,
  variant = 'normal',
}: BlogCardProps) {
  const delay = `${(index % 6) * 70}ms`;

  if (variant === 'featured') {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className={`group cursor-pointer block transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
        style={{ transitionDelay: isVisible ? delay : '0ms' }}
      >
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/25 hover:-translate-y-1 transition-all duration-300 grid grid-cols-1 md:grid-cols-2">
          <div className="relative h-64 md:h-auto overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            <span className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full font-body text-xs font-bold uppercase">
              {post.category}
            </span>
            {isNewPost(post.date) && (
              <div className="absolute top-4 right-4 flex items-center gap-1 bg-emerald-500 text-white pl-2 pr-2.5 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-wide">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70"></span>
                  <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-white"></span>
                </span>
                Novo
              </div>
            )}
          </div>
          <div className="p-8 flex flex-col justify-center">
            <p className="font-body text-xs text-gray-400 mb-3">{post.date}</p>
            <h2 className="font-heading font-bold text-2xl text-secondary group-hover:text-primary transition-colors mb-4 leading-snug">
              {post.title}
            </h2>
            <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <ReadTimeIndicator readTime={post.readTime} maxMinutes={maxReadMinutes} />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group cursor-pointer transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: isVisible ? delay : '0ms' }}
    >
      <div className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-200 border border-gray-100 hover:border-primary/25 flex flex-col h-full">
        {/* Thumbnail */}
        <div className="relative h-52 overflow-hidden flex-shrink-0">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

          <Link
            to={`/blog/categoria/${categoryToSlug(post.category)}`}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm text-white px-3 py-1 rounded-full font-body text-[10px] font-semibold uppercase hover:bg-primary transition-all cursor-pointer whitespace-nowrap"
          >
            {post.category}
          </Link>

          {isNewPost(post.date) && (
            <div className="absolute top-4 right-4 flex items-center gap-1 bg-emerald-500 text-white pl-2 pr-2.5 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-wide">
              <span className="relative flex w-1.5 h-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70"></span>
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-white"></span>
              </span>
              Novo
            </div>
          )}

          <span className="absolute bottom-3 left-4 font-body text-[10px] text-white/80 flex items-center gap-1">
            <i className="ri-calendar-line text-xs"></i>
            {post.date}
          </span>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-heading font-bold text-lg text-secondary group-hover:text-primary transition-colors mb-2 line-clamp-2 leading-snug">
            {post.title}
          </h3>
          <p className="font-body text-sm text-gray-500 line-clamp-3 mb-5 flex-1 leading-relaxed">
            {post.excerpt}
          </p>
          <ReadTimeIndicator readTime={post.readTime} maxMinutes={maxReadMinutes} />
        </div>
      </div>
    </Link>
  );
}